import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ContentChildren,
  EventEmitter,
  inject,
  input,
  Input,
  linkedSignal,
  Output,
  QueryList,
  signal,
  TemplateRef,
  ViewChild,
} from '@angular/core';

import { DxTextInput } from '@compeople-shared/design-system-angular';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import _ from 'lodash';
import { debounceTime, filter, Subject, tap } from 'rxjs';

import { AutocompleteTemplateComponent } from './autocomplete-template/autocomplete-template.component';

@UntilDestroy()
@Component({
  selector: 'spa-autocomplete-search',
  templateUrl: 'autocomplete-search.component.html',
  styleUrls: ['autocomplete-search.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutocompleteSearchComponent<T> {
  private readonly transloco = inject(TranslocoService);

  @ViewChild(DxTextInput, { static: true }) private searchInput!: DxTextInput;

  label = input<string>(this.transloco.translate('label.booking.guest-search'));
  isReadOnly = input<boolean>(false);
  @Input() checkboxSelectionType: 'single' | 'multiple' | 'none' = 'none';
  @Input() isSearching = false;
  @Input() isRequired = false;
  readonly rows = input<AutocompleteRow<T>[] | null>(null);
  protected readonly localRows = linkedSignal(() => this.rows());
  @Input() columns: AutocompleteColumn<T>[] = [];
  @Output() fetchData = new EventEmitter<string | null>();
  @Output() selectRow = new EventEmitter<T[] | undefined>();
  @ContentChildren(AutocompleteTemplateComponent) columnTemplates!: QueryList<AutocompleteTemplateComponent>;
  searchTermChange$ = new Subject<string>();
  protected readonly searchTerm = signal<string | null>(null);
  SEARCH_TERM_MIN_LENGTH = 2;
  icon = computed(() => (this.isReadOnly() ? '' : 'search'));

  protected readonly applyDisabled = computed(() => {
    const rows = this.localRows();
    return !rows || !_.size(rows.filter((row) => row.isChecked && !row.alreadySelected));
  });

  constructor() {
    this.searchTermChange$
      .pipe(
        tap((term) => {
          this.localRows.set(null);
          this.searchTerm.set(term);
        }),
        debounceTime(1000),
        filter((term: string) => term?.length > this.SEARCH_TERM_MIN_LENGTH),
        untilDestroyed(this),
      )
      .subscribe((term) => this.fetchData.emit(term));
  }

  cellValue(row: T, column: AutocompleteColumn<T>) {
    if (column.value) {
      return column.value(row);
    }
    // @ts-expect-error
    return row[column.key];
  }

  getTemplate(key: string | number | symbol) {
    return this.columnTemplates.find((t) => t.columnKey === key);
  }

  onFocus() {
    const term = this.searchTerm();
    if (term && term.length > this.SEARCH_TERM_MIN_LENGTH) {
      this.fetchData.emit(term);
    } else {
      this.localRows.set(null);
    }
  }
  onInputChange(e: CustomEvent) {
    this.searchTermChange$.next(e.detail);
  }

  onSelectSingleRowDirectly(row: AutocompleteRow<T>): void {
    if (!row.alreadySelected) {
      this.reset();
      this.selectRow.emit([row.data]);
      this.searchInput.focusControl();
    }
  }

  onCheck(index: number): void {
    this.localRows.update((rows) => {
      if (!rows) {
        return rows;
      }

      return rows.map((row, i) => {
        if (i === index) {
          return { ...row, isChecked: !row.isChecked };
        }
        if (this.checkboxSelectionType === 'single' && !row.alreadySelected) {
          return { ...row, isChecked: false };
        }
        return row;
      });
    });
  }

  reset() {
    this.localRows.set(null);
    this.searchTerm.set(null);
    this.fetchData.emit(null);
  }

  applySelection(): void {
    const rows = this.localRows();
    if (!rows) {
      return;
    }

    const data = rows
      .filter((value: AutocompleteRow<T>) => value.isChecked && !value.alreadySelected)
      .map((guest: AutocompleteRow<T>) => guest.data);

    this.reset();
    this.selectRow.emit(data);
    this.searchInput.focusControl();
  }

  enterpressed() {
    const hostElement = document.getElementById('guest-input-search');
    if (hostElement) {
      const shadowRoot = hostElement.shadowRoot;
      if (shadowRoot) {
        const inputElement = shadowRoot.getElementById('guest-input-search-control');
        if (inputElement) {
          inputElement.focus();
        }
      }
    }
  }
}

export interface AutocompleteRow<T> {
  isChecked: boolean;
  alreadySelected: boolean;
  data: T;
}

export interface AutocompleteColumn<T> {
  key: string | keyof T;
  value?: (row: T) => unknown;
  widthInPercent?: string;
  template?: TemplateRef<unknown>;
}
